import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

export const Route = createFileRoute("/(site)/rules")({
  component: Rules,
})

const tabs = [
  { id: "daily", label: "Daily" },
  { id: "champions", label: "Champions Game" },
  { id: "specialists", label: "Specialists" },
  { id: "pairs", label: "Pairs" },
  { id: "teams", label: "Teams" },
]

function Rules() {
  const [activeTab, setActiveTab] = useState("daily")

  useEffect(() => {
    window.scrollTo(0, 0)
    // Handle hash navigation on page load
    const hash = window.location.hash.replace("#", "")
    if (hash && tabs.some((tab) => tab.id === hash)) {
      setActiveTab(hash)
    }
  }, [])

  // Update URL hash when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value)
    window.history.replaceState(null, "", `#${value}`)
  }

  return (
    <div className="pt-24 pb-16 md:pt-32 md:pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">
            Rules & Competition Formats
          </h1>

          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="w-full flex flex-wrap h-auto gap-1 mb-8">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="flex-1 min-w-[100px]"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Daily */}
            <TabsContent value="daily">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    1. General Information
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Daily is the 10-week qualifying tournament culminating
                        in the Champions Game.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The Daily consists of <strong>50 game days</strong>.
                        Each game day is a <strong>10-question</strong> quiz on{" "}
                        <strong>kflquiz.com</strong>.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        All players are placed into divisions based on
                        performance. Current divisions are{" "}
                        <strong>
                          A, B1, B2, C1, C2, C3, D1, D2, D3, and D4
                        </strong>
                        .
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The maximum number of players in divisions is{" "}
                        <strong>24</strong>, but the D tier may have fewer or
                        more players.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The top <strong>8 players</strong> in each division
                        qualify for the Champions Game at the end of the Daily
                        season. Qualification is based on a player's{" "}
                        <strong>best eight weeks out of ten</strong>.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Four players with the lowest score in each division
                        (except the D tier) are relegated to the lower division.
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    2. How to Play the Daily
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Each game day, players receive a notification email
                        reminding them to play. The quiz itself is played on the
                        website.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Each Daily quiz is available during a{" "}
                        <strong>24-hour window</strong>. There is{" "}
                        <strong>no timer</strong> while answering, but answers
                        must be submitted before the window closes.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Before the final submission, you must{" "}
                        <strong>challenge</strong> any answers that were not
                        accepted automatically (if you believe they should be
                        accepted). Once you have submitted your challenges, no
                        further action is required.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Results are not issued immediately. Players should wait
                        until the next day to see the updated scores and
                        standings for all participants.
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    3. Scoring System
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The Daily scoring system does not assign a fixed "1
                        point" per correct answer. Instead, the value of a
                        correct answer depends on the difficulty of the
                        question, based on how many players answered it
                        correctly.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Example: if nearly all players answer a question
                        correctly, its value will be close to{" "}
                        <strong>1.00</strong>; if only a small percentage answer
                        correctly, its value will be higher.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        This system reduces split places in the standings and
                        rewards correct answers on more difficult questions.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* Champions Game */}
            <TabsContent value="champions">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    1. General Information
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Champions Game is KFL's flagship{" "}
                        <strong>individual</strong> competition.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The Champions Game consists of{" "}
                        <strong>200 questions</strong>, divided into{" "}
                        <strong>10 categories of 20 questions each</strong>,
                        reflecting KFL's Specialists subjects:{" "}
                        <strong>
                          History, Pop Music, Visual Arts, Lifestyle, Films &
                          TV, Geography, Science, Sports, Literature, and
                          Performing Arts
                        </strong>
                        .
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Champions Game is played on <strong>kflquiz.com</strong>
                        . No external platforms are required.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Each Champions Game is available to play during a{" "}
                        <strong>one-week window</strong>. Players may start the
                        game at any time during this window.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        All KFL subscribers may participate. However, only{" "}
                        <strong>qualified players</strong> are included in the{" "}
                        <strong>official standings</strong>. All other players
                        may play the Champions Game <strong>for fun</strong>{" "}
                        (outside official standings).
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    2. Game Format, Timing, and Challenges
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The Champions Game is played in{" "}
                        <strong>10 rounds</strong> (one per category), each
                        consisting of <strong>20 questions</strong>.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        <strong>Each round lasts 8 minutes.</strong> The timer
                        starts immediately when the player clicks "Start Quiz" /
                        "Next Round," and the round is{" "}
                        <strong>submitted automatically</strong> when time
                        expires.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Between rounds, players may take an{" "}
                        <strong>untimed break</strong> for any length of time
                        before starting the next round.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        After completing the game, players may{" "}
                        <strong>challenge</strong> answers that were not
                        accepted automatically. The admin reviews these
                        challenges.
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    3. Promotion Rules
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        By default, all divisions except Division A compete for
                        promotion to a higher division:
                      </span>
                    </li>
                    <li className="flex gap-2 ml-6">
                      <span className="text-muted-foreground">–</span>
                      <span>
                        <strong>
                          16 players in Division B tier (B1 and B2)
                        </strong>{" "}
                        compete for <strong>4 spots</strong> in Division A.
                      </span>
                    </li>
                    <li className="flex gap-2 ml-6">
                      <span className="text-muted-foreground">–</span>
                      <span>
                        <strong>
                          24 players in Division C tier (C1, C2, and C3)
                        </strong>{" "}
                        compete for <strong>8 spots</strong> in Division B tier.
                      </span>
                    </li>
                    <li className="flex gap-2 ml-6">
                      <span className="text-muted-foreground">–</span>
                      <span>
                        <strong>
                          32 players in Division D tier (D1, D2, D3, and D4)
                        </strong>{" "}
                        compete for <strong>12 spots</strong> in Division C
                        tier.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The number of promotion spots may increase in the
                        following cases:
                      </span>
                    </li>
                    <li className="flex gap-2 ml-6">
                      <span className="text-muted-foreground">–</span>
                      <span>
                        one or more players from a higher division terminate
                        their subscription;
                      </span>
                    </li>
                    <li className="flex gap-2 ml-6">
                      <span className="text-muted-foreground">–</span>
                      <span>
                        the organizer terminates the subscription of one or more
                        players from a higher division;
                      </span>
                    </li>
                    <li className="flex gap-2 ml-6">
                      <span className="text-muted-foreground">–</span>
                      <span>
                        a player informs the organizer that they do not plan to
                        play Daily in the near future and requests to drop.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        If two or more players who qualify for promotion are
                        tied, the tie is broken by{" "}
                        <strong>
                          the player with the higher Daily total after 10 weeks
                        </strong>
                        . If players are still tied, the organizer will make a
                        further decision and announce it.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* Specialists */}
            <TabsContent value="specialists">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    1. General Information
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Specialists is an <strong>individual</strong>{" "}
                        competition consisting of <strong>11 subjects</strong>:{" "}
                        <strong>
                          History, Films & TV, Geography, Lifestyle, Visual
                          Arts, Pop Music, Science, Sports, Performing Arts,
                          Literature
                        </strong>
                        , and <strong>Mystery Box</strong>.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Each Specialists quiz consists of{" "}
                        <strong>50 questions</strong>, played in{" "}
                        <strong>one timed round of 20 minutes</strong>. When the
                        time expires, answers are submitted automatically;
                        marking follows.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        One quiz in each subject is played each quarter. This
                        means <strong>11 Specialists quizzes per quarter</strong>{" "}
                        and <strong>44 Specialists quizzes per year</strong> (4
                        per subject).
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Each Specialists quiz is available to play during a{" "}
                        <strong>one-week window</strong>.
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    2. Subcategories
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    The following subcategories describe the expected scope of
                    questions in each subject:
                  </p>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      <strong className="text-foreground">History:</strong>{" "}
                      Prehistory, Ancient Era, Medieval Era, Modern Era,
                      Contemporary Era, Auxiliary Sciences of History
                      (Archaeology, Heraldry, Phaleristics, etc.), Current
                      Politics.
                    </p>
                    <p>
                      <strong className="text-foreground">Films & TV:</strong>{" "}
                      Cinema (1890s–present), TV (1930s–present), Directors,
                      Cinematographers, Documentaries, Screenwriters, Film
                      Studios, Producers, Awards, Animation, Cartoons,
                      Soundtracks, Film Composers.
                    </p>
                    <p>
                      <strong className="text-foreground">Geography:</strong>{" "}
                      Physical Geography, Human Geography, Space (not
                      Astronomy), Explorers, Auxiliary Sciences of Geography.
                    </p>
                    <p>
                      <strong className="text-foreground">Lifestyle:</strong>{" "}
                      Food & Drinks, Fashion, Brands, Digital, Business,
                      Tourism, Video Games (not competitive), Board Games (not
                      competitive), Health & Fitness, New Age Beliefs, Religion.
                    </p>
                    <p>
                      <strong className="text-foreground">Visual Arts:</strong>{" "}
                      Painting, Artists, Pottery/Ceramics, Architecture, Art
                      Galleries/Museums, Photography, Sculpture, Art History,
                      Auction Houses, Art Awards, Art Genres/Movements,
                      Calligraphy.
                    </p>
                    <p>
                      <strong className="text-foreground">Pop Music:</strong>{" "}
                      Songs, Performers, Albums, Bands, Record Labels, Venues,
                      Events, Producers, Genres.
                    </p>
                    <p>
                      <strong className="text-foreground">Science:</strong>{" "}
                      Formal Sciences (Mathematics, Statistics, Computer
                      Science, Logic, Data Science), Natural Sciences (Physics,
                      Chemistry, Astronomy, Geology, Biology, Medicine,
                      Pharmacy), Social Sciences (Anthropology, Economics,
                      Linguistics, Political Science, Psychology, Sociology,
                      Pedagogy, Jurisprudence, Philosophy), Flora & Fauna.
                    </p>
                    <p>
                      <strong className="text-foreground">Sports:</strong>{" "}
                      Summer Sports, Winter Sports, Motorsports, Board Games
                      (competitive), Esports, Records, Awards.
                    </p>
                    <p>
                      <strong className="text-foreground">
                        Performing Arts:
                      </strong>{" "}
                      Classical Music, Composers, Ballet, Opera, Performers,
                      Theatre, Musicals, Awards, Circus, Illusionists, Musical
                      Instruments, Dance, Music Theory.
                    </p>
                    <p>
                      <strong className="text-foreground">Literature:</strong>{" "}
                      Books, Authors, Comic Books, Newspapers & Magazines,
                      Awards, Poetry, Graphic Novels, Literary Theory,
                      Mythology.
                    </p>
                    <p>
                      <strong className="text-foreground">Mystery Box:</strong>{" "}
                      No subcategories. Mystery Box is a random-topic
                      Specialists quiz, and players discover the topic only
                      after they start the quiz.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    3. Access and Tickets
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        <strong>
                          Mystery Box is included in all subscription tiers.
                        </strong>
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        <strong>Gold members and First-timer members</strong>{" "}
                        have access to{" "}
                        <strong>all 11 Specialists quizzes</strong> each
                        quarter.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        <strong>Silver members</strong> receive{" "}
                        <strong>5 Specialists tickets per quarter</strong>. Each
                        ticket grants access to one Specialists quiz of their
                        choice (excluding Mystery Box).
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        <strong>Additional tickets</strong> may be purchased for{" "}
                        <strong>$3.50 per ticket</strong>. This option is
                        available to{" "}
                        <strong>
                          Silver members, Bronze members, and non-subscribers
                        </strong>
                        .
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    4. Determining Winners
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Each subject has <strong>four games per year</strong>{" "}
                        (one per quarter).
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The winner for a subject is the player with the highest
                        total score across their{" "}
                        <strong>best three games</strong> of that subject. If a
                        player participates in all four games, their lowest
                        result is ignored.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Tie-breaks are applied only to determine podium places
                        (top 3). If two or more players are tied for 1st–3rd
                        place after best three games, the total across{" "}
                        <strong>all four games</strong> is used. If they remain
                        tied, they share the place.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* Pairs */}
            <TabsContent value="pairs">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    1. General Information
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The Pairs competition runs for 12 months, from January
                        to December of the calendar year.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Pairs is a competition for teams of two players who
                        discuss and answer questions together. All questions are
                        answered on <strong>kflquiz.com</strong>; there is no
                        external quiz platform.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Partners may communicate using any platform they prefer.
                        KFL does not provide Zoom links for Pairs games.
                        However, KFL <strong>does</strong> provide Discord voice
                        channels: you can join our Discord server at{" "}
                        <a
                          href="https://discord.gg/WKJ6kvjNku"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          discord.gg/WKJ6kvjNku
                        </a>{" "}
                        and use any of the six available voice channels for your
                        game.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        A pair may consist of players of the same nationality or
                        different nationalities. In the standings, pairs with
                        different nationalities are marked as{" "}
                        <strong>International</strong>.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Players may change partners during the season. Each pair
                        partnership is treated as a separate entry in the
                        standings, and results are totalled separately for each
                        partnership.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Each Pairs game consists of 100 questions, divided into
                        five rounds of 20 questions. Each round is timed (10
                        minutes) and starts immediately when a pair clicks
                        "Start Quiz" / "Next Round." Between rounds, pairs may
                        take an untimed break for any length of time before
                        starting the next round.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The season consists of <strong>12 games</strong>:{" "}
                        <strong>3 games per quarter</strong>. Each game is
                        available to play during a{" "}
                        <strong>one-week window</strong>. Pairs do not play
                        head-to-head; they schedule and play independently
                        during the week.{" "}
                        <strong>
                          Results and standings for that game are published only
                          after the one-week window closes
                        </strong>
                        , once all participating pairs have finished.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Pairs is included at no extra cost for{" "}
                        <strong>
                          Gold members, Silver members, and First-timer members
                        </strong>
                        . Other players must pay <strong>$5 per game</strong>.
                        After payment, a player receives{" "}
                        <strong>tickets</strong> on their account;{" "}
                        <strong>
                          one ticket is consumed when entering a Pairs game
                        </strong>
                        .
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    2. Scheduling and Playing
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        For each Pairs game, the pair first agrees on a
                        convenient time, then schedules the game on{" "}
                        <strong>kflquiz.com</strong> within the one-week
                        availability window. At the scheduled time, they start
                        the game and play it on the website.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Each round is automatically submitted when the timer
                        expires. After the game, players may challenge answers
                        that were not accepted automatically; the admin reviews
                        these challenges.
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    3. Determining Winners
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The winners are the pair with the highest total score
                        across their <strong>best nine games</strong> of the
                        season.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Tie-breaks are applied only to determine podium places
                        (top 3). If two or more pairs are tied for 1st–3rd place
                        after best nine games, totals across best 10, then best
                        11, then all 12 games are compared. If pairs remain tied
                        after 12 games, they share the place.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        For all other ties (outside the top 3), pairs with the
                        same best-nine total share their position in the
                        standings.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* Teams */}
            <TabsContent value="teams">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    1. General Information
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The Teams competition runs for 12 months, from January
                        to December of the calendar year.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Teams is a competition where players discuss and answer
                        questions together on <strong>kflquiz.com</strong>. All
                        questions are answered on the website; there is no
                        external quiz platform.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Teams may communicate using any platform they prefer.
                        KFL does not provide Zoom links for Teams games.
                        However, KFL <strong>does</strong> provide Discord voice
                        channels: you can join our Discord server at{" "}
                        <a
                          href="https://discord.gg/WKJ6kvjNku"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          discord.gg/WKJ6kvjNku
                        </a>{" "}
                        and use any of the six available voice channels for your
                        game.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Teams may consist of players of the same nationality or
                        different nationalities. In the final standings, teams
                        with different nationalities are marked as{" "}
                        <strong>International</strong>.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        A team's full roster may include up to{" "}
                        <strong>8 players</strong>. In any given game,{" "}
                        <strong>3–5 players</strong> may play at the same time.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Substitutions and roster changes are allowed at any time
                        during the season. There is no limit to the number of
                        changes. However,{" "}
                        <strong>
                          a player may not leave a team and then re-join the
                          same team again within the same season
                        </strong>{" "}
                        (they may re-join the following year).
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        Each Teams game consists of 100 questions, divided into
                        five rounds of 20 questions. Each round is timed (10
                        minutes) and starts immediately when the team clicks
                        "Start Quiz" / "Next Round." Between rounds, teams may
                        take an untimed break for any length of time before
                        starting the next round.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        The season consists of <strong>12 games</strong>:{" "}
                        <strong>3 games per quarter</strong>. Each game is
                        available to play during a{" "}
                        <strong>one-week window</strong>. Teams do not play
                        head-to-head; they schedule and play independently
                        during the week.{" "}
                        <strong>
                          Results and standings for that game are published only
                          after the one-week window closes
                        </strong>
                        , once all participating teams have finished.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>Participation and pricing:</span>
                    </li>
                    <li className="flex gap-2 ml-6">
                      <span className="text-muted-foreground">–</span>
                      <span>
                        <strong>Free access:</strong> Gold members and
                        First-timer members.
                      </span>
                    </li>
                    <li className="flex gap-2 ml-6">
                      <span className="text-muted-foreground">–</span>
                      <span>
                        <strong>Pay-per-game:</strong> All other players pay{" "}
                        <strong>$5 per game</strong>. After payment, the account
                        receives <strong>tickets</strong>;{" "}
                        <strong>
                          one ticket is consumed when entering a Teams game
                        </strong>
                        .
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    2. Registration and Scheduling
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        All team creation, roster management, scheduling, and
                        gameplay are handled on <strong>kflquiz.com</strong>.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        New teams may join the season at any point. Please note
                        that the season standings are based on a team's{" "}
                        <strong>best nine games</strong>, so teams joining later
                        may be disadvantaged in the overall title race.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>
                        For each Teams game, the team agrees internally on a
                        convenient time and schedules the game on{" "}
                        <strong>kflquiz.com</strong> (subject to the game's
                        availability and the team's access/tickets). At the
                        scheduled time, the team starts the game and plays it on
                        the website.
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    3. Point System and How Winners Are Determined
                  </h3>
                  <div className="space-y-4">
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">
                        Points per game (by placing):
                      </strong>{" "}
                      The Teams points system is based on a relay-style ranking
                      system. Points are awarded as follows:
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-muted-foreground">
                        <tbody>
                          <tr>
                            <td className="py-1 pr-4">1st place</td>
                            <td className="py-1 pr-8 font-medium">420 pts</td>
                            <td className="py-1 pr-4">11th place</td>
                            <td className="py-1 pr-8 font-medium">210 pts</td>
                            <td className="py-1 pr-4">21st place</td>
                            <td className="py-1 font-medium">110 pts</td>
                          </tr>
                          <tr>
                            <td className="py-1 pr-4">2nd place</td>
                            <td className="py-1 pr-8 font-medium">390 pts</td>
                            <td className="py-1 pr-4">12th place</td>
                            <td className="py-1 pr-8 font-medium">200 pts</td>
                            <td className="py-1 pr-4">22nd place</td>
                            <td className="py-1 font-medium">100 pts</td>
                          </tr>
                          <tr>
                            <td className="py-1 pr-4">3rd place</td>
                            <td className="py-1 pr-8 font-medium">360 pts</td>
                            <td className="py-1 pr-4">13th place</td>
                            <td className="py-1 pr-8 font-medium">190 pts</td>
                            <td className="py-1 pr-4">23rd place</td>
                            <td className="py-1 font-medium">90 pts</td>
                          </tr>
                          <tr>
                            <td className="py-1 pr-4">4th place</td>
                            <td className="py-1 pr-8 font-medium">330 pts</td>
                            <td className="py-1 pr-4">14th place</td>
                            <td className="py-1 pr-8 font-medium">180 pts</td>
                            <td className="py-1 pr-4">24th place</td>
                            <td className="py-1 font-medium">80 pts</td>
                          </tr>
                          <tr>
                            <td className="py-1 pr-4">5th place</td>
                            <td className="py-1 pr-8 font-medium">310 pts</td>
                            <td className="py-1 pr-4">15th place</td>
                            <td className="py-1 pr-8 font-medium">170 pts</td>
                            <td className="py-1 pr-4">25th place</td>
                            <td className="py-1 font-medium">70 pts</td>
                          </tr>
                          <tr>
                            <td className="py-1 pr-4">6th place</td>
                            <td className="py-1 pr-8 font-medium">290 pts</td>
                            <td className="py-1 pr-4">16th place</td>
                            <td className="py-1 pr-8 font-medium">160 pts</td>
                            <td className="py-1 pr-4">26th place</td>
                            <td className="py-1 font-medium">60 pts</td>
                          </tr>
                          <tr>
                            <td className="py-1 pr-4">7th place</td>
                            <td className="py-1 pr-8 font-medium">270 pts</td>
                            <td className="py-1 pr-4">17th place</td>
                            <td className="py-1 pr-8 font-medium">150 pts</td>
                            <td className="py-1 pr-4">27th place</td>
                            <td className="py-1 font-medium">50 pts</td>
                          </tr>
                          <tr>
                            <td className="py-1 pr-4">8th place</td>
                            <td className="py-1 pr-8 font-medium">250 pts</td>
                            <td className="py-1 pr-4">18th place</td>
                            <td className="py-1 pr-8 font-medium">140 pts</td>
                            <td className="py-1 pr-4">28th place</td>
                            <td className="py-1 font-medium">40 pts</td>
                          </tr>
                          <tr>
                            <td className="py-1 pr-4">9th place</td>
                            <td className="py-1 pr-8 font-medium">230 pts</td>
                            <td className="py-1 pr-4">19th place</td>
                            <td className="py-1 pr-8 font-medium">130 pts</td>
                            <td className="py-1 pr-4">29th place</td>
                            <td className="py-1 font-medium">30 pts</td>
                          </tr>
                          <tr>
                            <td className="py-1 pr-4">10th place</td>
                            <td className="py-1 pr-8 font-medium">220 pts</td>
                            <td className="py-1 pr-4">20th place</td>
                            <td className="py-1 pr-8 font-medium">120 pts</td>
                            <td className="py-1 pr-4">30th place</td>
                            <td className="py-1 font-medium">20 pts</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <p className="font-semibold mb-3">Season winners:</p>
                      <ul className="space-y-3 text-muted-foreground">
                        <li className="flex gap-2">
                          <span className="text-primary">•</span>
                          <span>
                            The winners are the teams with the highest total
                            points across their <strong>best nine games</strong>{" "}
                            of the season. If a team plays more than nine games,
                            their lowest results are ignored.
                          </span>
                        </li>
                        <li className="flex gap-2">
                          <span className="text-primary">•</span>
                          <span>
                            <strong>
                              Tie-break system (same as in Pairs):
                            </strong>{" "}
                            tie-breaks are applied only to determine podium
                            places (top 3). If two or more teams are tied for
                            1st–3rd place after best nine games, totals across{" "}
                            <strong>best 10 games</strong> are compared; if
                            still tied, <strong>best 11</strong>, then{" "}
                            <strong>all 12</strong>. If still tied after 12
                            games, the teams share the place.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
